import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const empty = { name: "", organization: "", email: "", phone: "", city: "", message: "" };

export const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-[13px] font-semibold text-[#14083E]">{label}</Label>
    {children}
  </div>
);

export const inputCls = "h-12 rounded-xl bg-white border-[#14083E]/15 focus-visible:ring-[#E5157A] text-[#14083E]";

export const Success = ({ type, onReset }) => (
  <div className="text-center py-16" data-testid={`${type}-success`}>
    <CheckCircle2 className="w-14 h-14 mx-auto text-[#E5157A]" />
    <h3 className="mt-5 font-display text-3xl font-bold text-[#14083E]">{type === "patient" ? "Request received" : "Application received"}</h3>
    <p className="mt-3 text-slate-600 max-w-sm mx-auto">{type === "patient" ? "Our care advisor will call you on the number you shared to take this forward." : "Thanks for applying. A member of our network team will review your submission and call you shortly."}</p>
    <button onClick={onReset} data-testid={`${type}-new-application-btn`} className="mt-8 rounded-full border border-[#14083E]/20 px-6 py-3 font-semibold text-[#14083E] hover:bg-[#14083E] hover:text-white transition-colors">
      Submit another
    </button>
  </div>
);

export default function EnrolmentForm({ type, cfg }) {
  const [v, setV] = useState(empty);
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const missing = cfg.fields.find((f) => !f.optional && !details[f.key]);
    if (missing) return toast.error(`Please fill "${missing.label}"`);
    setLoading(true);
    try {
      await api.post(`/enrolments/${type}`, { ...v, details });
      setDone(true);
      setV(empty);
      setDetails({});
      toast.success("Enrolment submitted successfully");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) return <Success type={type} onReset={() => setDone(false)} />;

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid={`${type}-form`}>
      <Field id="name" label="Full name">
        <Input id="name" required value={v.name} onChange={set("name")} className={inputCls} data-testid={`${type}-name-input`} />
      </Field>
      <Field id="org" label={cfg.orgLabel}>
        <Input id="org" required={cfg.orgRequired} value={v.organization} onChange={set("organization")} className={inputCls} data-testid={`${type}-organization-input`} />
      </Field>
      <Field id="email" label="Email">
        <Input id="email" type="email" required value={v.email} onChange={set("email")} className={inputCls} data-testid={`${type}-email-input`} />
      </Field>
      <Field id="phone" label="Phone">
        <Input id="phone" type="tel" required minLength={6} value={v.phone} onChange={set("phone")} className={inputCls} data-testid={`${type}-phone-input`} />
      </Field>
      <Field id="city" label="City / State">
        <Input id="city" required value={v.city} onChange={set("city")} className={inputCls} data-testid={`${type}-city-input`} />
      </Field>
      {cfg.fields.map((f) => (
        <Field key={f.key} id={f.key} label={f.label}>
          {f.options ? (
            <Select value={details[f.key] || ""} onValueChange={(val) => setDetails({ ...details, [f.key]: val })}>
              <SelectTrigger id={f.key} className={inputCls} data-testid={`${type}-${f.key}-select`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {f.options.map((o) => <SelectItem key={o} value={o} data-testid={`${type}-${f.key}-option-${o}`}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input id={f.key} placeholder={f.placeholder} value={details[f.key] || ""} className={inputCls}
              onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })} data-testid={`${type}-${f.key}-input`} />
          )}
        </Field>
      ))}
      <div className="sm:col-span-2">
        <Field id="message" label="Tell us more (optional)">
          <Textarea id="message" rows={4} value={v.message} onChange={set("message")} className="rounded-xl bg-white border-[#14083E]/15 focus-visible:ring-[#E5157A]" data-testid={`${type}-message-input`} />
        </Field>
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-slate-600">By submitting, you agree to be contacted by IMG Health about this enrolment.</p>
        <button type="submit" disabled={loading} data-testid={`${type}-submit-btn`}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-8 py-4 font-semibold text-white shadow-[0_18px_40px_-15px_rgba(229,21,122,0.8)] hover:scale-[1.03] active:scale-[0.98] transition-transform disabled:opacity-70">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Submit enrolment <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
        </button>
      </div>
    </form>
  );
}
